/**
 * printModels — build the table payloads for the NiV "הדפסה" buttons
 * (clinics tab / leads page). Pure functions over the same rows the screen
 * shows (already filtered + sorted), so the printout matches what the admin
 * sees. The server (functions/nivPrintHelper.js nivPrintListPdf) only
 * validates and renders — every label is decided here.
 *
 * Payload: { kind, title, subtitle, meta: string[], columns: [{label, width?, align?, wrap?}], rows: string[][] }
 */

import { PROSPECT_SOURCE_LABELS, PROSPECT_STAGE_LABELS } from "../../../../services/nivFirebase";
import { formatPhoneIL } from "../../../../utils/phoneUtils";
import {
  CONTACT_CHANNEL_LABELS,
  formatDate,
  formatShekels,
  getContactStatus,
  getNextPayment,
  getPayingSummary,
  relativeDaysLabel,
  sumClinicRevenue,
} from "./nivDerived";

const NOTE_MAX = 160;

const clip = (text, max = NOTE_MAX) => {
  const t = String(text || "").replace(/\s+/g, " ").trim();
  if (!t) return "";
  return t.length > max ? `${t.slice(0, max - 1)}…` : t;
};

const formatDateTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString("he-IL", {
    timeZone: "Asia/Jerusalem",
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const nextActionCell = (nextAction) => {
  if (!nextAction) return "";
  const parts = [];
  if (nextAction.at) parts.push(formatDateTime(nextAction.at));
  if (nextAction.text) parts.push(clip(nextAction.text, 120));
  return parts.join(" · ");
};

const phoneCell = (phone) => {
  if (!phone) return "";
  try {
    return formatPhoneIL(phone) || phone;
  } catch {
    return phone;
  }
};

/**
 * Clinics table — one row per clinic in the current segment/filter/sort.
 * @param rows overview rows (filtered + sorted)
 * @param usageMap clinicId → usage row (logins.daysSinceLastLogin)
 * @param pendingSoByClinic clinicId → pending הו"ק months
 * @param meta.segmentLabel / planLabel / tagLabels / searchTerm / sortLabel — chips on the printout
 */
export const buildClinicsPrintPayload = (rows, usageMap = {}, pendingSoByClinic = {}, meta = {}) => {
  const columns = [
    { label: "קליניקה", width: 11, wrap: true },
    { label: "בעלים", width: 8, wrap: true },
    { label: "טלפון", width: 7, align: "ltr" },
    { label: "מסלול", width: 6 },
    { label: "תשלום", width: 9, wrap: true },
    { label: "תשלום הבא", width: 7 },
    { label: 'סה"כ הכנסות', width: 6 },
    { label: "מטופלים", width: 5 },
    { label: "קשר אחרון", width: 7 },
    { label: "כניסה אחרונה", width: 6 },
    { label: "פעולה הבאה", width: 10, wrap: true },
    { label: "הערות", width: 12, wrap: true },
  ];

  const body = rows.map((row) => {
    const crm = row.crm || {};
    const paying = getPayingSummary(crm, row.planEndDate);
    const next = getNextPayment(row, pendingSoByClinic[row.clinicId] || 0);
    const revenue = sumClinicRevenue(crm);
    const contact = getContactStatus(row);
    const loginDays = usageMap[row.clinicId]?.logins?.daysSinceLastLogin;

    let paymentCell = paying.statusLabel;
    if (paying.status === "paying") {
      const bits = [formatShekels(paying.amountPreVat)];
      if (paying.cycleLabel) bits.push(paying.cycleLabel);
      if (paying.isStandingOrder) bits.push('הו"ק');
      paymentCell = bits.join(" · ");
    }

    let nextCell = "";
    if (next) {
      nextCell = formatDate(next.date);
      if (next.pendingMonths > 0) nextCell += ` (${next.pendingMonths} ממתינים)`;
      else if (next.kind === "planEnd") nextCell += " (סיום)";
    }

    const contactCell = contact.lastContactAt
      ? `${formatDate(contact.lastContactAt)}${contact.channel ? ` · ${CONTACT_CHANNEL_LABELS[contact.channel] || contact.channel}` : ""}`
      : "";

    const planCell = row.plan === "trial" && row.trialEndDate ? `ניסיון עד ${formatDate(row.trialEndDate)}` : row.planName || row.plan || "";

    return [
      row.name || "ללא שם",
      row.ownerName || "",
      phoneCell(row.ownerPhone),
      planCell,
      paymentCell,
      nextCell,
      revenue.count ? formatShekels(Math.round(revenue.preVat)) : "",
      `${row.activePatients ?? 0}/${row.totalPatients ?? 0}`,
      contactCell,
      loginDays === undefined ? "" : relativeDaysLabel(loginDays),
      nextActionCell(crm.nextAction),
      clip(crm.freeNotes),
    ];
  });

  const chips = [];
  if (meta.segmentLabel) chips.push(`קטע: ${meta.segmentLabel}`);
  if (meta.planLabel) chips.push(`מסלול: ${meta.planLabel}`);
  if (meta.tagLabels?.length) chips.push(`תגיות: ${meta.tagLabels.join(", ")}`);
  if (meta.noUsageOnly) chips.push("ללא שימוש בלבד");
  if (meta.searchTerm) chips.push(`חיפוש: ${meta.searchTerm}`);
  if (meta.sortLabel) chips.push(`מיון: ${meta.sortLabel}`);

  return {
    kind: "clinics",
    title: "קליניקות — רשימה להדפסה",
    subtitle: "סכומים לפני מע״מ · מטופלים = פעילים/סה״כ",
    meta: chips,
    columns,
    rows: body,
  };
};

/**
 * Leads table — one row per prospect in the current source/search/sort.
 * @param prospects board prospects (filtered + sorted)
 * @param meta.sourceLabel / searchTerm / sortLabel
 */
export const buildLeadsPrintPayload = (prospects, meta = {}) => {
  const columns = [
    { label: "שם", width: 11, wrap: true },
    { label: "טלפון", width: 8, align: "ltr" },
    { label: "קליניקה", width: 10, wrap: true },
    { label: "שלב", width: 7 },
    { label: "מקור", width: 8 },
    { label: "תאריך פנייה", width: 7 },
    { label: "פעילות אחרונה", width: 7 },
    { label: "עדכונים", width: 4 },
    { label: "פעולה הבאה", width: 12, wrap: true },
    { label: "הערות", width: 14, wrap: true },
  ];

  const body = prospects.map((p) => {
    const stage = p.stage || "lead";
    const clinicCell = p.clinicName || (p.clinicId ? "מקושר" : "");
    return [
      p.fullName || "ללא שם",
      phoneCell(p.phone || p.id),
      clinicCell,
      PROSPECT_STAGE_LABELS[stage] || stage,
      PROSPECT_SOURCE_LABELS[p.source] || p.source || "",
      formatDate(p.createdAt),
      formatDate(p.updatedAt || p.createdAt),
      String((p.notes || []).length || ""),
      nextActionCell(p.nextAction),
      clip(p.freeNotes),
    ];
  });

  const chips = [];
  if (meta.sourceLabel) chips.push(`מקור: ${meta.sourceLabel}`);
  if (meta.searchTerm) chips.push(`חיפוש: ${meta.searchTerm}`);
  if (meta.sortLabel) chips.push(`מיון: ${meta.sortLabel}`);

  return {
    kind: "leads",
    title: "לידים — רשימה להדפסה",
    subtitle: "כל השלבים · לפי הסינון והמיון שעל המסך",
    meta: chips,
    columns,
    rows: body,
  };
};
